import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KOL_SLOT_META, PLATFORM_META } from "@/lib/meta";
import { ShieldCheck, Rocket } from "lucide-react";

const PROOF_HINT = { x: "x.com/…/status/…", telegram: "t.me/…", reddit: "reddit.com/…", tiktok: "tiktok.com/@…/video/…", instagram: "instagram.com/p/…", youtube: "youtu.be/…" };

export default function KolMagic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [payout, setPayout] = useState("");
  const [shot, setShot] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/kol/magic/${token}`).then(({ data }) => setData(data)).catch((e) => setErr(e?.response?.data?.detail || "Invalid link.")), [token]);
  useEffect(() => { load(); }, [load]);

  const uploadShot = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/uploads/image", fd); setShot(data.path); toast.success("Screenshot attached"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };

  const act = async (action, body) => {
    setBusy(true);
    try { await api.post(`/kol/magic/${token}/${action}`, body || {}); toast.success("Done"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(false); }
  };

  if (err) return <div className="flex min-h-screen items-center justify-center bg-ink text-dust" data-testid="magic-error">{err}</div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-ink text-dust">Loading…</div>;
  const s = data.slot;
  const meta = KOL_SLOT_META[s.status] || { label: s.status, cls: "" };
  const plat = PLATFORM_META[s.platform] || { label: s.platform };
  const pack = data.asset_pack || {};
  const bound = s.payout_wallet && s.payout_verified;

  return (
    <div className="min-h-screen bg-ink px-4 py-10 text-cream" data-testid="kol-magic-page">
      <div className="mx-auto max-w-lg space-y-5">
        <div className="flex items-center gap-2 text-pulp"><Rocket size={20} /><span className="font-semibold tracking-tight">Wen Moon · KOL desk</span></div>
        <div className="rounded-2xl border border-line bg-surface p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-dust">Offer for</p>
              <p className="text-lg font-semibold" data-testid="magic-kol-name">{s.kol_name}</p>
              <p className="font-mono text-xs text-dust">{plat.label} · {s.handle}</p>
            </div>
            <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.cls}`} data-testid="magic-status">{meta.label}</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl bg-raised/40 p-3"><p className="text-[10px] uppercase text-dust">Slot price</p><p className="font-mono text-lg">${s.price_usd}</p></div>
            <div className="rounded-xl bg-raised/40 p-3"><p className="text-[10px] uppercase text-dust">You receive (85%)</p><p className="font-mono text-lg text-pulp">${(s.price_usd * (1 - (data.protocol_pct || 0.15))).toFixed(2)}</p></div>
          </div>
          <p className="mt-3 flex items-center gap-1 text-[11px] text-dust"><ShieldCheck size={12} className="text-pulp" /> Accept locks escrow. Post from your own account, submit the link, get paid on approval. Miss the deadline and the offer is void.</p>
        </div>

        <div className="rounded-2xl border border-line bg-surface p-6">
          <p className="mb-2 text-xs uppercase tracking-wider text-dust">Your content brief — ${data.launch.ticker}</p>
          <pre className="whitespace-pre-wrap rounded-xl bg-raised/40 p-3 text-xs text-cream" data-testid="magic-brief">{pack.text || pack.body || pack.caption || JSON.stringify(pack, null, 2)}</pre>
          <p className="mt-2 font-mono text-[11px] text-dust">CA: {data.launch.contract_address}</p>
        </div>

        <div className="rounded-2xl border border-line bg-surface p-6">
          <p className="mb-3 text-xs uppercase tracking-wider text-dust">Your actions</p>
          {s.status === "offered" && (
            <div className="flex gap-2">
              <Button disabled={busy} onClick={() => act("accept")} className="flex-1 bg-pulp text-ink hover:bg-pulp/90" data-testid="magic-accept">Accept offer</Button>
              <Button disabled={busy} variant="outline" onClick={() => act("decline")} data-testid="magic-decline">Decline</Button>
            </div>
          )}
          {s.status === "in_escrow" && (
            <div className="space-y-3">
              {!bound && (
                <div className="flex gap-2">
                  <Input value={payout} onChange={(e) => setPayout(e.target.value)} placeholder="Your payout wallet" className="bg-ink text-sm" data-testid="magic-payout" />
                  <Button disabled={busy || payout.length < 6} onClick={() => act("bind-payout", { wallet: payout })} data-testid="magic-bind">Bind</Button>
                </div>
              )}
              {bound && <p className="font-mono text-xs text-pulp">payout bound {s.payout_wallet.slice(0, 8)}… ✓</p>}
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={`Proof URL (${PROOF_HINT[s.platform] || "public link"})`} className="bg-ink text-sm" data-testid="magic-proof-url" />
              <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (mention the ticker or CA)" className="bg-ink text-sm" data-testid="magic-proof-caption" />
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-line bg-ink px-3 py-2 text-xs text-dust hover:text-cream" data-testid="magic-shot-label">
                {uploading ? "Uploading…" : shot ? "Screenshot attached ✓" : "+ Attach screenshot (optional)"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadShot(e.target.files?.[0])} data-testid="magic-shot-input" />
              </label>
              <Button disabled={busy || !url} onClick={() => act("proof", { url, caption, screenshot_path: shot })} className="w-full bg-ai text-ink hover:bg-ai/90" data-testid="magic-submit-proof">Submit proof</Button>
            </div>
          )}
          {s.status === "proof_submitted" && <p className="text-sm text-warn" data-testid="magic-awaiting">Proof submitted — awaiting the creator's approval.</p>}
          {s.status === "released" && <p className="text-sm text-pulp" data-testid="magic-paid">Paid ${(s.principal || 0).toFixed(2)} to {s.payout_wallet?.slice(0, 8)}… <span className="font-mono text-[10px] text-ai">SIMULATED</span></p>}
          {["declined", "expired", "refunded", "disputed", "slashed_burn"].includes(s.status) && <p className="text-sm text-dust">This offer is closed ({meta.label.toLowerCase()}).</p>}
        </div>
      </div>
    </div>
  );
}
